import React from 'react';
import { useField, useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import { SIGNATURE_STATUS } from 'constants/Global';
import { STATUS_LOADING, STATUS_FAILED } from 'constants/MediaConstants';
import useLocalUserPreferences from 'hooks/useLocalUserPreferences';
import type { Media } from 'modules/mediaLibrary/types';
import { createNotification } from 'modules/toast-notifications/actions';
import type { MediaListItem } from 'ui/base/MediaGallery/types';

import { SUBSCRIPTION } from 'utils/formUtils';

import { MediasUploader } from '..';
import DefaultContent from './Content';
import SignatureModal from './SignatureModal';
import type { MediaUploaderProps } from '../MediasUploader/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ContentComponent = React.ComponentType<any>;
type UploaderItem = MediaListItem & { file: File };
type UploaderRef = { clearQueue: () => void };

type SignatureMediaWrapperProps = {
   name: string,
   content?: ContentComponent,
   title: string,
   preferenceKey: string,
   contentTitle?: string,
   uploadParams?: object,
   disabled?: boolean,
   className?: string;
   onDelete?: (media: Media) => void,
} & MediaUploaderProps

const SignatureMediaWrapper: React.FC<SignatureMediaWrapperProps> = ({
  name,
  title,
  disabled,
  onDelete,
  className,
  contentTitle,
  uploadParams,
  preferenceKey,
  uploadURL,
  content: Content = DefaultContent,
  ...rest
}) => {
  const form = useForm();
  const dispatch = useDispatch();
  const uploaderRef = React.useRef<UploaderRef | null>(null);

  const { setPreference } = useLocalUserPreferences();
  const [displayModal, setDisplayModal] = React.useState<boolean>(false);

  const { value: media } = useField(`${name}.media`, SUBSCRIPTION.VALUE_ONLY).input;
  const { value: signature } = useField(`${name}.signatureBox`, SUBSCRIPTION.VALUE_ONLY).input;
  const { value: status } = useField(`${name}.status`, SUBSCRIPTION.VALUE_ONLY).input;

  const mediaList = React.useMemo(() => (media ? [media] : []), [media]);

  const debouncedError = useDebouncedCallback(() => {
    dispatch(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.admin.dealer.update.usersImport.failure',
          defaultMessage: 'An error has occurred while uploading the file',
        },
      })
    );
  }, 250);

  const clearData = React.useCallback(() => {
    form.batch(() => {
      form.change(`${name}.media`, undefined);
      form.change(`${name}.signatureBox`, undefined);
      form.change(`${name}.status`, undefined);
    });
  }, [name, form]);

  const handleUpload = (medias: UploaderItem[]) => {
    const newMedia = medias?.[medias.length - 1];
    if (newMedia?.isUploaded) {
      const blob = new Blob([newMedia.file], { type: 'application/pdf' });
      const path = URL.createObjectURL(blob);
      form.batch(() => {
        form.change(`${name}.media`, { ...newMedia, path });
        form.change(`${name}.signatureBox`, undefined);
        form.change(`${name}.status`, undefined);
      });
      setDisplayModal(true);
    } else {
      if (newMedia?.status === STATUS_FAILED) {
        // The error is debounced to avoid displaying the error message twice because of the media uploader
        debouncedError();
        uploaderRef.current?.clearQueue();
      }
      clearData();
    }
  };

  const handleBeforeUpload = React.useCallback(
    (onBeforeUpload: (file: File) => void) =>
      (file: File) => {
        form.batch(() => {
          form.change(`${name}.media`, { status: STATUS_LOADING });
          form.change(`${name}.signatureBox`, undefined);
          form.change(`${name}.status`, undefined);
        });
        return onBeforeUpload(file);
      },
    [name, form]
  );

  const handleConfirm = () => {
    const { path, ...mediaData } = media;
    URL.revokeObjectURL(path);
    form.batch(() => {
      form.change(`${name}.media`, mediaData.data);
      form.change(`${name}.status`, SIGNATURE_STATUS.UNSIGNED);
    });
    setDisplayModal(false);
    setPreference(preferenceKey, signature);
  };

  const handleClose = () => {
    clearData();
    setDisplayModal(false);
  };

  const render = React.useCallback((
    medias: MediaListItem[],
    _: ((media: MediaListItem) => void) | undefined,
    onDeleteMedia: ((media: MediaListItem) => void) | undefined,
    onRetry: ((media: MediaListItem) => void) | undefined,
    onBeforeUpload: ((file: File) => void) | undefined,
  ) => {
    const currentMedia = medias?.[medias.length - 1];
    const deleteFn = onDelete ?? onDeleteMedia;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ContentEl: React.ComponentType<any> = Content;

    return (
      <ContentEl
        onRetry={onRetry}
        onDelete={deleteFn}
        disabled={disabled}
        medias={medias}
        media={currentMedia}
        className={className}
        signatureBox={signature}
        signatureStatus={status}
        title={contentTitle ?? title}
        onBeforeUpload={onBeforeUpload ? handleBeforeUpload(onBeforeUpload) : undefined}
      />
    );
  }, [Content, disabled, status, contentTitle, title, handleBeforeUpload, className, signature, onDelete]);

  return (
    <div data-cy="document-upload">
      <MediasUploader
        onlyRender
        delegateToForm
        isTwoStepUpload
        render={render}
        // @ts-expect-error need to migrate MediasUploader to TypeScript
        ref={uploaderRef}
        medias={mediaList}
        readOnly={disabled}
        params={uploadParams}
        uploadURL={uploadURL}
        onUploaded={handleUpload}
        {...rest}
      />
      {displayModal && (
        <SignatureModal
          name={name}
          path={media.path}
          title={title}
          onCancel={handleClose}
          onOk={handleConfirm}
          preferenceKey={preferenceKey}
        />
      )}
    </div>
  );
};

export default SignatureMediaWrapper;
